import { keepPreviousData, useQuery } from '@tanstack/vue-query';

import { computed, onMounted, ref, toValue, watch, watchEffect } from 'vue';
import type { ComputedRef, Ref } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import { isInWechat, isIOS } from '@eaphone/env';
import type { AsyncList, Data, List, ListParams } from '@eaphone/network/lib';
import { setTitle } from '@eaphone/plugins';

export function useTitle() {
  const route = useRoute();

  const val = computed(() => {
    const { title, titleKey, titleMaps } = route.meta;

    return titleKey && route.params[titleKey] && titleMaps
      ? titleMaps[route.params[titleKey] as string] || title
      : title;
  });

  watchEffect(() => {
    setTitle(val.value);
  });

  onMounted(() => {
    if (isIOS && isInWechat) {
      // iOS 返回问题
      console.log(123);

      window.addEventListener('pageshow', (e) => {
        if (e.persisted) {
          setTitle(val.value);
        }
      });
    }
  });
}

export function useUserIdInRoute() {
  const { replace } = useRouter();
  const route = useRoute();

  return computed({
    get() {
      return route.params.userId as string;
    },
    set(value: string) {
      replace({
        params: {
          ...route.params,
          userId: value,
        },
      });
    },
  });
}

export function useUserIdInQuery() {
  const { replace } = useRouter();
  const route = useRoute();

  return computed({
    get() {
      return route.query.userId as string | undefined;
    },
    set(value?: string) {
      replace({
        query: {
          ...route.query,
          userId: value,
        },
      });
    },
  });
}

export interface UsePagerOptions<P extends Record<string, any>> {
  params?: ComputedRef<ListParams<P> | undefined>;
  pageSize?: number;
}

export type Jumper = {
  pageIndex: ComputedRef<number>;
  pageSize: number;
  prevable: ComputedRef<boolean>;
  nextable: ComputedRef<boolean>;
  jump: (page: number) => void;
  prev: () => void;
  next: () => void;
};

export interface UsePagerReturn<T extends Record<string, any>> {
  /** 加载状态 */
  loading: Ref<boolean>;
  /** 错误信息 */
  error: Ref<Error | undefined>;
  /** 数据 */
  state: Ref<List<T> | undefined>;
  jumper: Jumper;
}

/**
 * 分页查询 Hook
 * - 支持前后翻页
 * - params 变化时自动重置到第一页
 * - pageIndex 与 route.query.page_index 双向同步
 */
export function usePager<
  T extends Data,
  P extends Record<string, unknown> = Record<string, never>,
>(fetcher: AsyncList<T, P>, options: UsePagerOptions<P>): UsePagerReturn<T> {
  const { params, pageSize = 10 } = options;
  const route = useRoute();
  const router = useRouter();

  const internalPageIndex = ref(1);

  // pageIndex 与 URL 双向同步
  const pageIndex = computed({
    get: () => {
      const urlPageIndex = Number(route.query.page_index);

      return urlPageIndex > 0 ? urlPageIndex : internalPageIndex.value;
    },
    set: (value: number) => {
      internalPageIndex.value = value;
      router.replace({
        query: {
          ...route.query,
          page_index: value > 1 ? String(value) : undefined,
        },
      });
    },
  });

  // params 变化时重置到第一页
  watch(
    () => toValue(params),
    () => {
      pageIndex.value = 1;
    },
  );

  const queryResult = useQuery({
    queryKey: ['pager', params, pageIndex],
    queryFn: () => {
      const p = toValue(params);

      if (!p) {
        throw new Error('params is required');
      }

      return fetcher({
        ...p,
        pageIndex: pageIndex.value,
        pageSize,
      } as P & { pageIndex: number; pageSize: number });
    },
    enabled: () => Boolean(toValue(params)),
    placeholderData: keepPreviousData,
  });

  const prevable = computed(() => pageIndex.value > 1);
  const nextable = computed(() => {
    const total = queryResult.data.value?.total_record;

    if (!total) {
      return false;
    }

    return pageIndex.value * pageSize < total;
  });

  const jump = (page: number) => {
    if (page >= 1) {
      pageIndex.value = page;
    }
  };

  const next = () => {
    if (nextable.value) {
      jump(pageIndex.value + 1);
    }
  };

  const prev = () => {
    if (prevable.value) {
      jump(pageIndex.value - 1);
    }
  };

  const error = computed(() => queryResult.error.value ?? undefined);

  return {
    loading: queryResult.isLoading,
    error,
    state: queryResult.data,
    jumper: { pageIndex, pageSize, prevable, nextable, jump, prev, next },
  };
}
